import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Eye, EyeOff } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Icon } from './Icon';

export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: true;
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null | undefined;
  required?: boolean;
  /** Show "(optional)" after the label instead of marking required fields. */
  optional?: boolean;
  className?: string;
  id?: string;
  children: (control: FieldControlProps) => ReactNode;
}

/** Label above, hint and error below, all wired with aria-describedby (design system §4.4, §5.1). */
export function Field({ label, hint, error, required, optional, className, id: givenId, children }: FieldProps) {
  const { t } = useTranslation('common');
  const autoId = useId();
  const id = givenId ?? `f${autoId.replace(/:/g, '')}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const control: FieldControlProps = { id };
  if (describedBy) control['aria-describedby'] = describedBy;
  if (error) control['aria-invalid'] = true;
  if (required) control['aria-required'] = true;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium text-secondary">
        {label}
        {required ? (
          <span aria-hidden="true" className="ms-1 text-danger-text">
            *
          </span>
        ) : null}
        {optional ? <span className="ms-1 font-regular text-muted">{t('form.optional')}</span> : null}
      </label>
      {children(control)}
      {hint ? (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {/* Always-present live region so a new field error is announced (SC 4.1.3), not only when focused. */}
      <div aria-live="polite" aria-atomic="true">
        {error ? (
          <p id={errorId} className="flex items-start gap-1 text-sm text-danger-text">
            <Icon icon={CircleAlert} size={16} className="mt-0.5" />
            <span>{error}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}

const controlBase = cn(
  'w-full rounded-md border bg-surface-sunken px-3 text-md text-primary placeholder:text-muted',
  'transition-colors duration-micro hover:border-border-strong',
  'focus-visible:border-border-focus disabled:cursor-not-allowed disabled:bg-surface disabled:text-disabled',
  'aria-[invalid=true]:border-danger',
);

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Force an LTR island (email, URL, phone, codes, numbers) — a11y doc §11.5. */
  ltr?: boolean;
  inputSize?: 'md' | 'lg';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ltr, inputSize = 'md', dir, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      dir={ltr ? 'ltr' : (dir ?? 'auto')}
      className={cn(
        controlBase,
        inputSize === 'lg' ? 'h-control-lg' : 'h-control-md',
        'border-border-control text-start',
        ltr && 'bidi-plaintext',
        className,
      )}
      {...rest}
    />
  );
});

export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputProps, 'type'>>(function PasswordInput(
  { className, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);
  // The input is an LTR island (a11y §11.5). The wrapper must share that direction, otherwise in RTL the button's
  // `end-0` lands on the left while the input's `pe-12` reserves space on the right and the icon covers the text.
  return (
    <div className="relative" dir="ltr">
      <Input ref={ref} type={visible ? 'text' : 'password'} ltr className={cn('pe-12', className)} {...rest} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-pressed={visible}
        aria-label={visible ? t('form.hidePassword') : t('form.showPassword')}
        className="absolute inset-y-0 end-0 flex w-11 items-center justify-center rounded-e-md text-muted hover:text-primary"
      >
        <Icon icon={visible ? EyeOff : Eye} size={18} />
      </button>
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, rows = 3, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      dir="auto"
      rows={rows}
      className={cn(controlBase, 'min-h-[5.5rem] border-border-control py-2 text-start', className)}
      {...rest}
    />
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: readonly SelectOption[];
  placeholder?: string;
  inputSize?: 'md' | 'lg';
}

/** Native select: best keyboard, screen reader and phone behaviour for short lists (design system §4.6). */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, inputSize = 'md', ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(controlBase, inputSize === 'lg' ? 'h-control-lg' : 'h-control-md', 'border-border-control pe-8', className)}
      {...rest}
    >
      {placeholder !== undefined ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
});

type FieldShellProps = Pick<FieldProps, 'label' | 'hint' | 'error' | 'required' | 'optional' | 'className'>;

export const TextField = forwardRef<HTMLInputElement, FieldShellProps & InputProps>(function TextField(
  { label, hint, error, required, optional, className, ...input },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} optional={optional} className={className}>
      {(control) => <Input ref={ref} {...control} required={required} {...input} />}
    </Field>
  );
});

export const PasswordField = forwardRef<HTMLInputElement, FieldShellProps & Omit<InputProps, 'type'>>(function PasswordField(
  { label, hint, error, required, optional, className, ...input },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} optional={optional} className={className}>
      {(control) => <PasswordInput ref={ref} {...control} required={required} {...input} />}
    </Field>
  );
});

export function TextareaField({
  label,
  hint,
  error,
  required,
  optional,
  className,
  ...input
}: FieldShellProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <Field label={label} hint={hint} error={error} required={required} optional={optional} className={className}>
      {(control) => <Textarea {...control} required={required} {...input} />}
    </Field>
  );
}

export function SelectField({ label, hint, error, required, optional, className, ...select }: FieldShellProps & SelectProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required} optional={optional} className={className}>
      {(control) => <Select {...control} required={required} {...select} />}
    </Field>
  );
}
